import ConciergePanel from 'app/concierge/panel';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { RootState } from 'store/store';
import {
    hide,
    sendNewRequestMessage,
    setSelectedDomains,
    setPrependContext,
    show,
    startNewChat,
    toggleVisibility,
    setConciergeCustomEvents,
    IConciergeCustomEvents,
    setLaunchElement,
} from 'store/conciergeSlice';
import { useEffect } from 'react';
import { dispatchCustomEvent } from 'tools/events';
import { SxProps, Theme } from '@mui/material';

interface IProps {
    welcome_icon?: string;
    welcome_icon_width?: number;
    welcome_icon_height?: number;
    bot_icon?: string;
    title?: string;
    description?: string;
    start_new_chat_after?: number;
    show_source_selector?: boolean;
    display_model_name?: boolean;
    suggested_prompts?: string[];
    show_search_request?: boolean;
    include_content_search?: boolean;
    include_help_docs?: boolean;
    include_glossary_terms?: boolean;
    include_custom_faq?: boolean;
    available_domains?: string[];
    selected_domains?: string[];
    load_more?: boolean;
    extra_url_params?: { [key: string]: string | number | (string | number)[] };
    use_info_button?: boolean;
    custom_events?: IConciergeCustomEvents;
    custom_styles?: {
        panel?: SxProps<Theme>;
        welcomePage?: SxProps<Theme>;
        header?: SxProps<Theme>;
        footer?: SxProps<Theme>;
        activeChat?: SxProps<Theme>;
        activeChatMessage?: SxProps<Theme>;
        activeChatMessageResultItem?: SxProps<Theme>;
        recentChats?: SxProps<Theme>;
        recentChatsItem?: SxProps<Theme>;
        recentChatsMenu?: SxProps<Theme>;
    };
    custom_instructions?: {
        mcp_post_processing?: string;
        llm_comment?: string;
    };
}

interface ConciergePanelEvent {
    action: string;
    payload?: any;
    /** The report or catalog entry the panel is opened for; rides every ask as element_id. */
    element_id?: number;
    selected_domains?: string[];
    prepend_context?: {
        text: string;
        show_in_chat?: boolean;
    };
}

function normalizeExtraUrlParams(params?: {
    [key: string]: string | number | (string | number)[];
}): { [key: string]: string } | undefined {
    if (!params) return undefined;

    const result: { [key: string]: string } = {};
    for (const [key, value] of Object.entries(params)) {
        result[key] = Array.isArray(value) ? JSON.stringify(value) : String(value);
    }

    return result;
}

interface ActionResult {
    success: boolean;
    message?: string;
}

interface ICallbacks {
    getContext?: () => any | Promise<any>;
    onAction?: (action: string, payload: any) => Promise<ActionResult> | ActionResult;
}

export default function ConciergeWrapper({ props, callbacks }: { props: IProps; callbacks?: ICallbacks }) {
    const dispatch = useDispatch();

    useEffect(() => {
        const handler = (e: Event) => {
            if (e instanceof CustomEvent && e.detail) {
                const params = e.detail as ConciergePanelEvent;

                if (params.action === 'toggle-panel') {
                    dispatch(toggleVisibility());
                }
                // An opening event names the entry it is for, or none: a stale entry from an
                // earlier open never rides a later ask.
                if (['show-panel', 'start-new-chat', 'send-message'].includes(params.action)) {
                    dispatch(
                        setLaunchElement(params.element_id && params.element_id > 0 ? Number(params.element_id) : null),
                    );
                }
                if (params.action === 'show-panel') {
                    dispatch(show());
                    if (params.prepend_context?.text) {
                        dispatch(
                            setPrependContext({
                                text: params.prepend_context.text,
                                showInChat: params.prepend_context.show_in_chat ?? false,
                            }),
                        );
                    }
                }
                if (params.action === 'hide-panel') {
                    dispatch(hide());
                }
                if (params.action === 'send-message' && params.payload) {
                    dispatch(show());
                    if (Array.isArray(params.selected_domains)) {
                        dispatch(setSelectedDomains(params.selected_domains));
                    } else {
                        dispatch(setSelectedDomains([]));
                    }
                    dispatch(sendNewRequestMessage(params.payload));
                }
                if (params.action === 'start-new-chat') {
                    dispatch(show());
                    dispatch(startNewChat(''));
                    if (Array.isArray(params.selected_domains)) {
                        dispatch(setSelectedDomains(params.selected_domains));
                    }
                    if (params.prepend_context?.text) {
                        dispatch(
                            setPrependContext({
                                text: params.prepend_context.text,
                                showInChat: params.prepend_context.show_in_chat ?? false,
                            }),
                        );
                    }
                }
            }
        };
        document.addEventListener('mi-concierge-panel', handler as EventListener);

        return () => document.removeEventListener('mi-concierge-panel', handler as EventListener);
    }, [dispatch]);

    useEffect(() => {
        dispatch(setConciergeCustomEvents(props.custom_events ?? {}));
    }, [props.custom_events, dispatch]);

    const visible = useSelector((state: RootState) => state.concierge.visible);
    const store = useStore<RootState>();

    // Resizes report themselves from the panel; here only open/close, so width is read, not watched.
    useEffect(() => {
        dispatchCustomEvent('mi-concierge-panel', {
            action: 'panel-visibility-changed',
            payload: { isOpen: visible, width: store.getState().concierge.width },
        });
    }, [visible, store]);

    return (
        <ConciergePanel
            isAppPanel
            title={props.title}
            description={props.description}
            suggestedPrompts={props.suggested_prompts}
            botIcon={props.bot_icon}
            displayModelName={props.display_model_name}
            showSourceSelector={props.show_source_selector}
            startNewChatAfter={props.start_new_chat_after}
            welcomeIcon={props.welcome_icon}
            welcomeIconHeight={props.welcome_icon_height}
            welcomeIconWidth={props.welcome_icon_width}
            availableDomains={props.available_domains}
            selectedDomains={props.selected_domains}
            includeContentSearch={props.include_content_search}
            includeCustomFaq={props.include_custom_faq}
            includeGlossaryTerms={props.include_glossary_terms}
            includeHelpDocs={props.include_help_docs}
            showSearchRequest={props.show_search_request}
            loadMore={props.load_more}
            customStyles={props.custom_styles}
            extraUrlParams={normalizeExtraUrlParams(props.extra_url_params)}
            useInfoButton={props.use_info_button}
            getContext={callbacks?.getContext}
            customInstructions={props.custom_instructions}
            onAction={callbacks?.onAction}
        />
    );
}
